import * as React from "react";
import {TopHeader} from "./top.header";
import {Switch, Route} from "react-router-dom";
import {RouteComponentProps, StaticContext, withRouter, Redirect} from "react-router";
import {AppStore} from "../stores/app.store";
import {useAppStore} from "./app.store.consumer";
import {MainBlank} from "./blank";
import {NotFound} from "./not.found";
import {LoginState, Login} from "../login/login";
import {UserDropdown} from "../user-dropdown/user.dropdown";
import {PublicDebugPanel} from "../public/debug-panel/debug.panel";
import {SiteMapRoutes} from "../stores/site.map";
import Questionnaire from "../public/questionnaire/questionnaire";
import Home from "../home/home";
import ReportPage from "../public/report-page/report.page";
import {ProfessionalProfile} from "./constants";
import WithQuestionnaireType from "./with.questionnaire.type";
import GenericWrapper from "../generic-wrapper/generic.wrapper";
import CIHHeader from "../CIH/components/cih.header";
import CIHFooter from "../CIH/components/cih.footer";
import ReportPageV2 from "../public/report-page-v2/report.page";

// const Home = React.lazy(() => import("../home/home"));

interface ProtectedPageProps extends RouteComponentProps<any, StaticContext, any> {
	appStore?: AppStore;
	component: React.ExoticComponent | React.ComponentType;
	isAdminPage: boolean;
	questionnaireType: string;
}

interface ProtectedPageState {
	initialized: boolean;
	pathname: string;
}

@useAppStore()
class ProtectedPageWithoutRouter extends React.Component<ProtectedPageProps, ProtectedPageState> {
	state: ProtectedPageState = {
		initialized: false,
		pathname: null,
	};

	private _prevComponent: JSX.Element = null;

	async componentDidMount() {
		await this.handleUrlChange();
	}

	private async handleUrlChange() {
		this.setState({
			initialized: true,
			pathname: this.props.location.pathname,
		});
		window.scrollTo(0, 0);
	}

	static getDerivedStateFromProps(nextProps: Readonly<ProtectedPageProps>, prevState: Readonly<ProtectedPageState>): ProtectedPageState {
		if (nextProps.location.pathname !== prevState.pathname) {
			return {
				initialized: false,
				pathname: nextProps.location.pathname,
			};
		} else {
			return null;
		}
	}

	async componentDidUpdate(prevProps: ProtectedPageProps) {
		if (prevProps.location.pathname !== this.props.location.pathname) {
			await this.handleUrlChange();
		}
	}

	render() {
		let children: JSX.Element = null;
		let Component = this.props.component;
		if (this.state.initialized) {
			children = <Component />;
			this._prevComponent = children;
		} else {
			children = this._prevComponent ? this._prevComponent : <MainBlank />;
		}

		if (this.props.questionnaireType) {
			children = <WithQuestionnaireType questionnaireType={this.props.questionnaireType}>{children}</WithQuestionnaireType>;
		}

		return (
			<React.Fragment>
				<PublicDebugPanel />
				<UserDropdown />
				<CIHHeader />
				<TopHeader />
				<GenericWrapper>{children}</GenericWrapper>
				<CIHFooter />
			</React.Fragment>
		);
	}
}

const ProtectedPage = withRouter(ProtectedPageWithoutRouter);

/**
 * Wraps protected element with header and footer.
 * @param Component The component that is the main component of the page.
 */
const renderProtectedElement = (Component: React.ExoticComponent | React.ComponentType, isAdminPage?: boolean, questionnaireType?: string) => {
	return () => {
		return <ProtectedPage component={Component} isAdminPage={isAdminPage ?? false} questionnaireType={questionnaireType} />;
	};
};

const renderPublicElement = (Component: React.ExoticComponent | React.ComponentType, questionnaireType?: string) => {
	return renderProtectedElement(Component, false, questionnaireType);
};

const getLoginComponent = (state: LoginState) => {
	return () => <Login initialState={state} />;
};

export const ProfProfileRoutes = (baseUrl: string) => {
	return (
		<Switch>
			<Route exact path={`${baseUrl}${SiteMapRoutes.Questionnaire}`} render={renderPublicElement(Questionnaire, ProfessionalProfile)} />
			<Route exact path={`${baseUrl}${SiteMapRoutes.Report}`} render={renderPublicElement(ReportPage, ProfessionalProfile)} />
			<Route exact path={`${baseUrl}${SiteMapRoutes.ReportV2}`} render={renderPublicElement(ReportPageV2, ProfessionalProfile)} />
		</Switch>
	);
};

/** Defines the routes of the application. */
export const Routes = () => {
	return (
		<Switch>
			<Route exact path="/" render={renderProtectedElement(Home)} />
			<Route path={SiteMapRoutes.ProfessionalProfile} render={({match}) => ProfProfileRoutes(match.url)} />
			<Route path="/not-found" component={NotFound} />
			<Route path="/login/:redirectUrl?" component={getLoginComponent(LoginState.Default)} />
			<Route path="/session-expired/:redirectUrl?" component={getLoginComponent(LoginState.SessionExpired)} />
			<Route path="/access-link-expired/:redirectUrl?" component={getLoginComponent(LoginState.AccessLinkExpired)} />
			<Route path="/forced-logout/:redirectUrl?" component={getLoginComponent(LoginState.ForcedLogout)} />
			<Route path="/access-link-not-found/:redirectUrl?" component={getLoginComponent(LoginState.AccessLinkNotFound)} />
			<Route path="/access-denied/:redirectUrl?" component={getLoginComponent(LoginState.AccessDenied)} />
			<Redirect to="/not-found" />
		</Switch>
	);
};
